import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

export interface LanguageContextType {
  currentLanguage: string
  supportedLanguages: string[]
  setLanguage: (lang: string) => void
  t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

interface LanguageProviderProps {
  children: ReactNode
  initialLanguage?: string
  supportedLanguages?: string[]
}

// UI translations dictionary
const translations: Record<string, Record<string, string>> = {
  it: {
    // Common
    'common.loading': 'Caricamento...',
    'common.error': 'Errore',
    'common.success': 'Successo',
    'common.cancel': 'Annulla',
    'common.confirm': 'Conferma',
    'common.save': 'Salva',
    'common.close': 'Chiudi',
    'common.search': 'Cerca',
    'common.all': 'Tutti',

    // Menu
    'menu.title': 'Menu',
    'menu.search': 'Cerca piatti...',
    'menu.products_count': '{count} prodotti',
    'menu.product_count': '{count} prodotto',
    'menu.no_products': 'Nessun prodotto trovato',
    'menu.unavailable': 'Non disponibile',
    'menu.table': 'Tavolo',

    // Cart
    'cart.title': 'Carrello',
    'cart.empty': 'Il carrello è vuoto',
    'cart.item_added': '{name} aggiunto',
    'cart.subtotal': 'Subtotale',
    'cart.tax': 'IVA',
    'cart.total': 'Totale',
    'cart.view': 'Vedi Carrello',
    'cart.checkout': 'Procedi',
    'cart.add': 'Aggiungi',
    'cart.quantity': 'Quantità',
    'cart.remove': 'Rimuovi',

    // Product
    'product.price': 'Prezzo',
    'product.allergens': 'Allergeni',
    'product.no_allergens': 'Nessun allergene',
    'product.description': 'Descrizione',
    'product.prep_time': 'Tempo preparazione',
    'product.minutes': '{count} min',

    // Checkout
    'checkout.title': 'Checkout',
    'checkout.payment': 'Pagamento',
    'checkout.cash': 'Pago in cassa',
    'checkout.card': 'Paga con carta',
    'checkout.order_success': 'Ordine inviato!',
    'checkout.order_error': 'Errore nell\'ordine',

    // Language selector
    'language.select': 'Seleziona lingua',
  },
  en: {
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.save': 'Save',
    'common.close': 'Close',
    'common.search': 'Search',
    'common.all': 'All',

    // Menu
    'menu.title': 'Menu',
    'menu.search': 'Search dishes...',
    'menu.products_count': '{count} products',
    'menu.product_count': '{count} product',
    'menu.no_products': 'No products found',
    'menu.unavailable': 'Unavailable',
    'menu.table': 'Table',

    // Cart
    'cart.title': 'Cart',
    'cart.empty': 'Cart is empty',
    'cart.item_added': '{name} added',
    'cart.subtotal': 'Subtotal',
    'cart.tax': 'Tax',
    'cart.total': 'Total',
    'cart.view': 'View Cart',
    'cart.checkout': 'Checkout',
    'cart.add': 'Add',
    'cart.quantity': 'Quantity',
    'cart.remove': 'Remove',

    // Product
    'product.price': 'Price',
    'product.allergens': 'Allergens',
    'product.no_allergens': 'No allergens',
    'product.description': 'Description',
    'product.prep_time': 'Prep time',
    'product.minutes': '{count} min',

    // Checkout
    'checkout.title': 'Checkout',
    'checkout.payment': 'Payment',
    'checkout.cash': 'Pay at counter',
    'checkout.card': 'Pay with card',
    'checkout.order_success': 'Order placed!',
    'checkout.order_error': 'Order error',

    // Language selector
    'language.select': 'Select language',
  },
  fr: {
    // Common
    'common.loading': 'Chargement...',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.cancel': 'Annuler',
    'common.confirm': 'Confirmer',
    'common.save': 'Enregistrer',
    'common.close': 'Fermer',
    'common.search': 'Rechercher',
    'common.all': 'Tous',

    // Menu
    'menu.title': 'Menu',
    'menu.search': 'Rechercher des plats...',
    'menu.products_count': '{count} produits',
    'menu.product_count': '{count} produit',
    'menu.no_products': 'Aucun produit trouvé',
    'menu.unavailable': 'Indisponible',
    'menu.table': 'Table',

    // Cart
    'cart.title': 'Panier',
    'cart.empty': 'Le panier est vide',
    'cart.item_added': '{name} ajouté',
    'cart.subtotal': 'Sous-total',
    'cart.tax': 'TVA',
    'cart.total': 'Total',
    'cart.view': 'Voir le panier',
    'cart.checkout': 'Commander',
    'cart.add': 'Ajouter',
    'cart.quantity': 'Quantité',
    'cart.remove': 'Supprimer',

    // Product
    'product.price': 'Prix',
    'product.allergens': 'Allergènes',
    'product.no_allergens': 'Aucun allergène',
    'product.description': 'Description',
    'product.prep_time': 'Temps de préparation',
    'product.minutes': '{count} min',

    // Checkout
    'checkout.title': 'Paiement',
    'checkout.payment': 'Paiement',
    'checkout.cash': 'Payer au comptoir',
    'checkout.card': 'Payer par carte',
    'checkout.order_success': 'Commande envoyée!',
    'checkout.order_error': 'Erreur de commande',

    // Language selector
    'language.select': 'Sélectionner la langue',
  },
  de: {
    // Common
    'common.loading': 'Wird geladen...',
    'common.error': 'Fehler',
    'common.success': 'Erfolg',
    'common.cancel': 'Abbrechen',
    'common.confirm': 'Bestätigen',
    'common.save': 'Speichern',
    'common.close': 'Schließen',
    'common.search': 'Suchen',
    'common.all': 'Alle',

    // Menu
    'menu.title': 'Menü',
    'menu.search': 'Gerichte suchen...',
    'menu.products_count': '{count} Produkte',
    'menu.product_count': '{count} Produkt',
    'menu.no_products': 'Keine Produkte gefunden',
    'menu.unavailable': 'Nicht verfügbar',
    'menu.table': 'Tisch',

    // Cart
    'cart.title': 'Warenkorb',
    'cart.empty': 'Warenkorb ist leer',
    'cart.item_added': '{name} hinzugefügt',
    'cart.subtotal': 'Zwischensumme',
    'cart.tax': 'MwSt',
    'cart.total': 'Gesamt',
    'cart.view': 'Warenkorb ansehen',
    'cart.checkout': 'Zur Kasse',
    'cart.add': 'Hinzufügen',
    'cart.quantity': 'Menge',
    'cart.remove': 'Entfernen',

    // Product
    'product.price': 'Preis',
    'product.allergens': 'Allergene',
    'product.no_allergens': 'Keine Allergene',
    'product.description': 'Beschreibung',
    'product.prep_time': 'Zubereitungszeit',
    'product.minutes': '{count} min',

    // Checkout
    'checkout.title': 'Kasse',
    'checkout.payment': 'Zahlung',
    'checkout.cash': 'An der Kasse bezahlen',
    'checkout.card': 'Mit Karte bezahlen',
    'checkout.order_success': 'Bestellung aufgegeben!',
    'checkout.order_error': 'Bestellfehler',

    // Language selector
    'language.select': 'Sprache wählen',
  },
  es: {
    // Common
    'common.loading': 'Cargando...',
    'common.error': 'Error',
    'common.success': 'Éxito',
    'common.cancel': 'Cancelar',
    'common.confirm': 'Confirmar',
    'common.save': 'Guardar',
    'common.close': 'Cerrar',
    'common.search': 'Buscar',
    'common.all': 'Todos',

    // Menu
    'menu.title': 'Menú',
    'menu.search': 'Buscar platos...',
    'menu.products_count': '{count} productos',
    'menu.product_count': '{count} producto',
    'menu.no_products': 'No se encontraron productos',
    'menu.unavailable': 'No disponible',
    'menu.table': 'Mesa',

    // Cart
    'cart.title': 'Carrito',
    'cart.empty': 'El carrito está vacío',
    'cart.item_added': '{name} añadido',
    'cart.subtotal': 'Subtotal',
    'cart.tax': 'IVA',
    'cart.total': 'Total',
    'cart.view': 'Ver carrito',
    'cart.checkout': 'Proceder',
    'cart.add': 'Añadir',
    'cart.quantity': 'Cantidad',
    'cart.remove': 'Eliminar',

    // Product
    'product.price': 'Precio',
    'product.allergens': 'Alérgenos',
    'product.no_allergens': 'Sin alérgenos',
    'product.description': 'Descripción',
    'product.prep_time': 'Tiempo de preparación',
    'product.minutes': '{count} min',

    // Checkout
    'checkout.title': 'Pago',
    'checkout.payment': 'Pago',
    'checkout.cash': 'Pagar en caja',
    'checkout.card': 'Pagar con tarjeta',
    'checkout.order_success': '¡Pedido enviado!',
    'checkout.order_error': 'Error en el pedido',

    // Language selector
    'language.select': 'Seleccionar idioma',
  },
}

export const LanguageProvider = ({ children, initialLanguage = 'it', supportedLanguages = ['it', 'en'] }: LanguageProviderProps) => {
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    // Try to get language from localStorage
    const saved = localStorage.getItem('qorder_language')
    return saved || initialLanguage
  })

  const [supported, setSupported] = useState<string[]>(supportedLanguages)

  useEffect(() => {
    // Save language to localStorage when it changes
    localStorage.setItem('qorder_language', currentLanguage)
  }, [currentLanguage])

  const setLanguage = (lang: string) => {
    if (supported.includes(lang)) {
      setCurrentLanguage(lang)
    }
  }

  // Translation function with placeholder support
  const t = (key: string, params?: Record<string, any>): string => {
    const langDict = translations[currentLanguage] || translations['it']
    let text = langDict[key] || key

    // Replace placeholders like {count}, {name}, etc.
    if (params) {
      Object.keys(params).forEach(param => {
        text = text.replace(`{${param}}`, String(params[param]))
      })
    }

    return text
  }

  const value: LanguageContextType = {
    currentLanguage,
    supportedLanguages: supported,
    setLanguage,
    t,
  }

  // Allow updating supported languages from outside
  useEffect(() => {
    setSupported(supportedLanguages)
  }, [supportedLanguages])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

// Hook to use language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
